import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { ensureDeviceFp } from './deviceFingerprint';
import { getClientId } from './clientId';

const BASE_URL = '/api';

export const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  timeout: 30_000,
});

let memoryAccessToken: string | null = null;

export function setAccessToken(token: string | null) {
  memoryAccessToken = token;
}

export function getAccessToken() {
  return memoryAccessToken;
}

api.interceptors.request.use(async (config) => {
  const url = config.url ?? '';
  // 反盗版：所有请求注入设备指纹头（已 memoize，命中即时；首个请求至多等 800ms）
  const fp = await ensureDeviceFp();
  if (fp) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>)['X-Device-FP'] = fp;
  }
  // 持久客户端 ID 第二通道：localStorage 镜像回注，供服务端在 cookie 丢失时自愈
  const cid = getClientId();
  if (cid) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>)['X-Client-Id'] = cid;
  }
  // Admin 路径走 AdminAuthContext 的 token 注入，用户 token 不覆盖
  if (url.startsWith('/admin/') || url.startsWith('admin/')) {
    return config;
  }
  if (memoryAccessToken) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>)['Authorization'] = `Bearer ${memoryAccessToken}`;
  }
  return config;
});

let refreshing: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (!refreshing) {
    refreshing = api
      .post<{ accessToken: string }>('/auth/refresh')
      .then((res) => {
        const newToken = res.data?.accessToken ?? null;
        setAccessToken(newToken);
        return newToken;
      })
      .catch(() => {
        setAccessToken(null);
        return null;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
    if (error.response?.status === 401 && original && !original._retry && !original.url?.includes('/auth/')) {
      original._retry = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
        return api.request(original);
      }
    }
    // 手机未验证：后端对耗权益动作返回 403 PHONE_VERIFICATION_REQUIRED，
    // 全局派发事件，由 PhoneVerificationModalProvider 监听弹框。
    const data = error.response?.data as { code?: string } | undefined;
    if (error.response?.status === 403 && data?.code === 'PHONE_VERIFICATION_REQUIRED') {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('zx:phone-required'));
      }
    }
    return Promise.reject(error);
  },
);
